import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API, thumbUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 12;

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: (currency || "gbp").toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, expired, failed
  const [payload, setPayload] = useState(null);
  const attempts = useRef(0);
  const { clearCart } = useCart();

  // NOTE: MAX_ATTEMPTS / POLL_INTERVAL are module constants and `api` is a module
  // import — all referentially stable, so [sessionId, clearCart] is complete.
  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayload(data);
          setStatus("paid");
          // Clear the basket after successful payment
          clearCart();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) setStatus("failed");
        else setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clearCart]);

  const items = payload?.items || [];
  const total = items.reduce((s, it) => s + Number(it.amount || 0), 0);
  const currency = items[0]?.currency || "gbp";
  const isMulti = items.length > 1;

  return (
    <div className="max-w-3xl mx-auto px-6 pt-24 pb-32 text-center" data-testid="success-page">
      {status === "checking" && (
        <>
          <p className="text-xs uppercase tracking-[0.4em] pm-orange mb-6 font-semibold">Confirming payment</p>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">One moment…</h1>
          <p className="text-white/50 mt-6 leading-relaxed font-condensed">Verifying your transaction with Stripe. This usually takes a few seconds.</p>
          <div className="mt-12 flex justify-center">
            <div className="h-px w-32 bg-white/20 overflow-hidden">
              <div className="h-full w-1/3 bg-[var(--pm-orange)] animate-pulse" />
            </div>
          </div>
        </>
      )}

      {status === "paid" && (
        <>
          <p className="text-xs uppercase tracking-[0.4em] pm-orange mb-6 font-semibold">Payment received</p>
          <h1 className="font-display text-6xl md:text-7xl uppercase leading-none">
            Thank <span className="pm-orange">you</span>.
          </h1>
          <p className="text-white/70 mt-6 leading-relaxed font-condensed">
            {isMulti
              ? `${items.length} photos are ready to download below.`
              : "Your purchase is complete. The full-resolution file is ready for download below."}
          </p>

          {items.length > 0 && (
            <div className="mt-12 text-left space-y-3" data-testid="success-items">
              {items.map((it) => (
                <div key={it.order_id} className="flex items-center gap-4 border-2 border-white/10 p-3 bg-[#0a0a0a]" data-testid={`success-item-${it.order_id}`}>
                  <img src={thumbUrl(it.image_id)} alt={it.image_title} className="w-24 h-24 object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-xl uppercase leading-tight truncate">{it.image_title}</p>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-condensed mt-1">{formatPrice(it.amount, it.currency)}</p>
                  </div>
                  <a
                    href={`${API}/orders/${it.order_id}/download?token=${encodeURIComponent(it.download_token)}`}
                    className="pm-btn shrink-0 text-xs"
                    data-testid={`download-${it.order_id}`}
                  >
                    Download
                  </a>
                </div>
              ))}
              {payload?.discount_total > 0 && (
                <div className="flex items-baseline justify-between pt-3" data-testid="success-discount">
                  <span className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Discount · {payload.discount_code}</span>
                  <span className="font-display text-xl uppercase pm-orange">−{formatPrice(payload.discount_total, currency)}</span>
                </div>
              )}
              {isMulti && (
                <div className="flex items-baseline justify-between pt-4 border-t-2 border-[var(--pm-orange-40)]">
                  <span className="text-xs uppercase tracking-[0.3em] text-white/60 font-semibold">Total paid</span>
                  <span className="font-display text-3xl uppercase">{formatPrice(total, currency)}</span>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-white/40 mt-8 font-condensed">
            Keep this page bookmarked — download links remain valid using your unique token.
          </p>

          <div className="mt-12">
            <Link to="/" className="text-xs uppercase tracking-[0.3em] pm-orange hover:text-white font-semibold" data-testid="continue-browsing">
              ← Continue browsing
            </Link>
          </div>
        </>
      )}

      {status === "expired" && (
        <>
          <h1 className="font-display text-5xl uppercase">Session expired</h1>
          <p className="text-white/50 mt-6 font-condensed">Your checkout session has expired. Please try again.</p>
          <Link to="/" className="pm-btn inline-block mt-10">Back to gallery</Link>
        </>
      )}

      {status === "failed" && (
        <>
          <h1 className="font-display text-5xl uppercase">Something went wrong</h1>
          <p className="text-white/50 mt-6 font-condensed">We couldn&apos;t confirm your payment. If you were charged, contact support.</p>
          <Link to="/" className="pm-btn inline-block mt-10">Back to gallery</Link>
        </>
      )}
    </div>
  );
}
